import { Fragment, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { useAssetTypes } from '../../api/queries'
import { useLocale } from '../../contexts/LocaleContext'
import { getLocalizedName } from '../../lib/localization'
import { lotBreakdown } from '../../lib/lot-breakdown'

/** The list a part links to: a subtree, or what an import built. */
export type LotBreakdownScope = { ancestor_uid: string } | { import_uid: string }

interface LotBreakdownProps {
    org: string
    /** Lot counts keyed by asset type code; non-lot codes are ignored. */
    counts: Record<string, number> | null | undefined
    scope: LotBreakdownScope
    testId: string
    showTotal?: boolean
    className?: string
}

const KNOWN = new Set(['unit_housing', 'unit_commercial', 'unit_parking', 'unit_cellar', 'unit'])

/**
 * Lots counted by nature on one wrapping line (spec 2026-10-11-19, pattern
 * "Count by nature"): an optional total, then each nature in a fixed order,
 * each linked to the assets list on its exact type in the same scope. The
 * total is not a link: the list cannot filter every lot type at once.
 * Test ids: `{testId}-line` (the line), `{testId}-total`, `{testId}-{code}`.
 */
export function LotBreakdown({ org, counts, scope, testId, showTotal = false, className }: LotBreakdownProps) {
    const { t } = useTranslation('assets')
    const { locale } = useLocale()
    const { total, parts } = lotBreakdown(counts)
    // An organization's own subtype is named from the ontology.
    const { data: assetTypes = [] } = useAssetTypes(org)
    const names = useMemo(() => {
        const byCode: Record<string, string> = {}
        for (const at of assetTypes as Array<{ code?: string; name?: Record<string, string> }>) {
            if (at.code) byCode[at.code] = getLocalizedName(at.name, locale) || at.code
        }
        return byCode
    }, [assetTypes, locale])

    if (total === 0) return null

    const label = (code: string, count: number) =>
        KNOWN.has(code)
            ? t(`lot_breakdown.${code}`, { count })
            : t('lot_breakdown.other', { count, name: names[code] || code })

    return (
        <p className={`text-sm text-foreground ${className ?? ''}`} data-testid={`${testId}-line`}>
            {showTotal && (
                <span className="whitespace-nowrap font-medium" data-testid={`${testId}-total`}>
                    {t('lot_breakdown.total', { count: total })}
                </span>
            )}
            {parts.map((p, i) => (
                <Fragment key={p.code}>
                    {(showTotal || i > 0) && <span className="text-muted-foreground"> · </span>}
                    <Link
                        to="/$org/assets"
                        params={{ org }}
                        search={{
                            q: undefined,
                            flag: undefined,
                            asset_type_code: p.code,
                            asset_type_uid: undefined,
                            showBuildingGroups: undefined,
                            showBuildings: undefined,
                            archived: undefined,
                            ...scope,
                        }}
                        data-testid={`${testId}-${p.code}`}
                        className="whitespace-nowrap hover:text-primary hover:underline"
                    >
                        {label(p.code, p.count)}
                    </Link>
                </Fragment>
            ))}
        </p>
    )
}
